/**
 * Mini-page dédiée à une étape des Fêtes populaires de saint Léonard.
 * URL: /fetes-internationales/:slug
 */
import { useParams, Link, Navigate } from "react-router-dom";
import Ornament from "@/components/Ornament";
import SeoHead from "@/components/SeoHead";
import { findFeteBySlug, FETES_LOCATIONS_SORTED } from "@/data/fetesInternationales";

export default function FetesInternationalesDetail() {
  const { slug } = useParams();
  const loc = findFeteBySlug(slug);

  if (!loc) {
    return <Navigate to="/fetes-internationales" replace />;
  }

  // Compute prev/next for cross-navigation between stops
  const idx = FETES_LOCATIONS_SORTED.findIndex((l) => l.slug === slug);
  const prev = idx > 0 ? FETES_LOCATIONS_SORTED[idx - 1] : null;
  const next = idx < FETES_LOCATIONS_SORTED.length - 1 ? FETES_LOCATIONS_SORTED[idx + 1] : null;

  const seoTitle = `${loc.city}, ${loc.country} — Fêtes populaires de saint Léonard · ${loc.dates}`;

  return (
    <>
      <SeoHead
        titles={{ fr: seoTitle, en: seoTitle }}
        descriptions={{
          fr: `${loc.city} (${loc.country}), ${loc.dates}. ${loc.resonance}`,
          en: `${loc.city} (${loc.country}), ${loc.dates}. ${loc.resonance}`,
        }}
      />
      <div data-testid={`fete-detail-${loc.slug}`} style={{ background: "var(--parchment)" }}>
        {/* HERO */}
        <section
          className="relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${loc.hero_color} 0%, ${loc.hero_color}dd 100%)`,
            minHeight: 420,
          }}
        >
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,16,12,0.15) 0%, rgba(20,16,12,0.55) 100%)" }} />
          <div className="relative z-10 max-w-4xl mx-auto px-6 py-24 sm:py-32 text-center text-white">
            <Ornament light />
            <div className="text-5xl mb-4 mt-4" aria-hidden>{loc.flag}</div>
            <p className="font-caps text-[0.72rem]" style={{ letterSpacing: "0.22em", color: "#fff", opacity: 0.9 }}>
              {loc.country.toUpperCase()} · MILLENIUM 2027
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-3" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.5)" }}>
              {loc.city}
            </h1>
            <p className="font-body text-xl sm:text-2xl mt-4" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.5)" }}>
              {loc.dates}
            </p>
            <div
              className="inline-block mt-6 font-caps text-xs"
              style={{
                letterSpacing: "0.22em",
                background: "#fff",
                color: loc.accent_color,
                padding: "6px 14px",
                fontWeight: 600,
              }}
              data-testid={`fete-theme-${loc.slug}`}
            >
              {loc.theme.toUpperCase()}
            </div>
          </div>
        </section>

        {/* Résonance */}
        <section className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
          <div className="text-center">
            <p className="font-caps text-[0.66rem]" style={{ color: "var(--gold-dark, #a68b3d)", letterSpacing: "0.22em" }}>
              RÉSONANCE
            </p>
            <div className="w-24 h-px mx-auto mt-2 mb-6" style={{ background: loc.accent_color }} />
          </div>
          <p className="font-body text-lg sm:text-xl leading-relaxed" style={{ color: "var(--ink-2)" }}>
            {loc.resonance}
          </p>
          {loc.host && (
            <div className="mt-10 pt-6 border-t" style={{ borderColor: "rgba(20,16,12,0.12)" }}>
              <div className="font-caps text-[0.62rem]" style={{ color: "var(--gold-dark, #a68b3d)", letterSpacing: "0.18em" }}>
                PARTENAIRE LOCAL
              </div>
              <div className="font-display text-xl mt-1">{loc.host}</div>
            </div>
          )}
        </section>

        {/* Prev / Next */}
        <section className="max-w-4xl mx-auto px-6 pb-16 flex flex-col sm:flex-row justify-between gap-4">
          {prev ? (
            <Link to={`/fetes-internationales/${prev.slug}`} data-testid="fete-prev" className="block flex-1 p-4 border transition-colors" style={{ borderColor: "var(--border)", textDecoration: "none" }}>
              <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.2em" }}>← ÉTAPE PRÉCÉDENTE</div>
              <div className="font-display text-lg mt-1" style={{ color: "var(--ink)" }}>{prev.flag} {prev.city}</div>
              <div className="font-body text-sm" style={{ color: "var(--muted)" }}>{prev.dates}</div>
            </Link>
          ) : <div className="flex-1" />}
          {next ? (
            <Link to={`/fetes-internationales/${next.slug}`} data-testid="fete-next" className="block flex-1 p-4 border text-right transition-colors" style={{ borderColor: "var(--border)", textDecoration: "none" }}>
              <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.2em" }}>ÉTAPE SUIVANTE →</div>
              <div className="font-display text-lg mt-1" style={{ color: "var(--ink)" }}>{next.city} {next.flag}</div>
              <div className="font-body text-sm" style={{ color: "var(--muted)" }}>{next.dates}</div>
            </Link>
          ) : <div className="flex-1" />}
        </section>

        {/* Retour à l'index */}
        <section className="py-12 px-6 text-center border-t" style={{ borderColor: "var(--gold)", background: "var(--parchment-2, #f4ecd8)" }}>
          <Link to="/fetes-internationales" data-testid="fete-back-index" className="btn btn-outline">
            ← Toutes les étapes
          </Link>
        </section>
      </div>
    </>
  );
}
